import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/AdminHeader';
import { getContactMessages, markMessageRead, deleteContactMessage } from '../../services/api';
import { toast } from 'sonner';
import { Mail, MailOpen, Trash2, ArrowLeft } from 'lucide-react';

const AdminMessages = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const data = await getContactMessages();
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markMessageRead(id);
      toast.success('Message marked as read');
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
      toast.error('Failed to mark message as read');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    setDeleting(id);
    try {
      await deleteContactMessage(id);
      toast.success('Message deleted successfully');
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
        <AdminHeader />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400 text-lg">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <AdminHeader />

      <main className="max-w-[1200px] mx-auto px-8 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">BACK</span>
        </button>

        <h1 className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 font-['Poppins'] text-5xl font-black mb-8">
          CONTACT MESSAGES
        </h1>

        {messages.length === 0 ? (
          <div className="glass-effect rounded-2xl p-12 text-center border border-white/10">
            <Mail className="mx-auto text-gray-500 mb-4" size={64} />
            <p className="text-white font-['Poppins'] text-2xl font-bold mb-2">
              NO MESSAGES YET
            </p>
            <p className="text-gray-400">
              Messages from your contact form will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`glass-effect rounded-2xl p-6 border transition-all ${
                  message.read ? 'border-white/10' : 'border-purple-500/50 shadow-lg shadow-purple-500/20'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {message.read ? (
                      <MailOpen className="text-gray-500" size={24} />
                    ) : (
                      <Mail className="text-purple-400" size={24} />
                    )}
                    <div>
                      <h3 className="text-white font-['Poppins'] text-xl font-bold">
                        {message.name}
                      </h3>
                      <p className="text-gray-400 text-sm">{message.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 mb-2">
                      {formatDate(message.created_at)}
                    </p>
                    <div className="flex gap-2 justify-end">
                      {!message.read && (
                        <button
                          onClick={() => handleMarkRead(message.id)}
                          className="text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all font-medium"
                        >
                          Mark as Read
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(message.id)}
                        disabled={deleting === message.id}
                        className="text-xs bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 transition-all font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={12} />
                        {deleting === message.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 p-4 rounded-xl border-l-4 border-purple-500">
                  <p className="text-gray-300 whitespace-pre-wrap">
                    {message.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminMessages;